import { Suspense, useMemo, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery, usePaginationFragment } from "react-relay"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { BetterDocExchangeFilters } from "@/modules/admin/better-doc/components/better-doc-exchange-filters"
import { BetterDocExchangeTable } from "@/modules/admin/better-doc/components/better-doc-exchange-table"
import { LIST_PAGE_SIZE } from "@/modules/admin/better-doc/constants/better-doc-constants"
import {
  BetterDocExchangeFilterValues,
  buildBetterDocExchangeFilter,
  INITIAL_EXCHANGE_FILTER_VALUES,
} from "@/modules/admin/better-doc/lib/better-doc-exchange-filter"
import { BetterDocExchangesPaginationQuery } from "@/modules/admin/better-doc/services/__generated__/BetterDocExchangesPaginationQuery.graphql"
import { betterDocExchangesQuery as BetterDocExchangesQueryType } from "@/modules/admin/better-doc/services/__generated__/betterDocExchangesQuery.graphql"
import { betterDocExchangesQueryListFragment$key } from "@/modules/admin/better-doc/services/__generated__/betterDocExchangesQueryListFragment.graphql"
import {
  BetterDocExchangeFilter,
  betterDocExchangesQuery,
  betterDocExchangesQueryListFragment,
} from "@/modules/admin/better-doc/services/betterDocExchangesQuery"

type BetterDocPaginatedExchangeTableProps = {
  filter: BetterDocExchangeFilter
}

const BetterDocPaginatedExchangeTable = ({ filter }: BetterDocPaginatedExchangeTableProps) => {
  const { t } = useTranslation()

  const queryData = useLazyLoadQuery<BetterDocExchangesQueryType>(
    betterDocExchangesQuery,
    { filter, first: LIST_PAGE_SIZE },
    { fetchPolicy: "network-only" },
  )
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    BetterDocExchangesPaginationQuery,
    betterDocExchangesQueryListFragment$key
  >(betterDocExchangesQueryListFragment, queryData)

  const exchanges = data.exchanges?.edges?.map((edge) => edge.node) ?? []

  return (
    <div className="flex flex-col gap-4">
      <BetterDocExchangeTable
        exchanges={exchanges}
        emptyMessage={t("admin.betterDoc.exchangeTable.emptyForFilter")}
        testId="better-doc-admin-exchange-list-table"
      />
      {hasNext ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="secondary"
            isLoading={isLoadingNext}
            disabled={isLoadingNext}
            onClick={() => loadNext(LIST_PAGE_SIZE)}
            data-testid="better-doc-admin-exchange-list-load-more">
            {t("admin.betterDoc.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export const BetterDocExchangeList = () => {
  const { t } = useTranslation()
  const [filterValues, setFilterValues] = useState<BetterDocExchangeFilterValues>(INITIAL_EXCHANGE_FILTER_VALUES)
  const filter = useMemo(() => buildBetterDocExchangeFilter(filterValues), [filterValues])

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-exchange-list">
      <BetterDocExchangeFilters filterValues={filterValues} onFilterValuesChange={setFilterValues} />
      <ErrorBoundary
        resetKeys={[filter]}
        fallback={
          <Banner type="inline" variant="critical" data-testid="better-doc-admin-exchange-list-error">
            {t("admin.betterDoc.requestFailed")}
          </Banner>
        }>
        <Suspense fallback={<LoadingSpinner size={24} className="flex justify-center p-8" />}>
          <BetterDocPaginatedExchangeTable filter={filter} />
        </Suspense>
      </ErrorBoundary>
    </div>
  )
}
